"use client";

import Image from "next/image";
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";

import { devSectionAttr } from "@/lib/dev/section-attr";
import { useCcPrefersReducedMotion } from "@/lib/use-cc-prefers-reduced-motion";
import { useCcScrollEffect } from "@/lib/use-cc-scroll-effect";

type SceneItem =
  | { id: string; kind: "photo"; photo: { src: string; alt: string } }
  | { id: string; kind: "card"; card: ReactNode };

type WhyChooseUsSceneMotionProps = {
  labelledBy: string;
  /** Shown one after another before the heading takes over. */
  statements: ReactNode[];
  heading: ReactNode;
  items: SceneItem[];
};

/**
 * Resting place of each item around the statement, as % of the stage, plus
 * its photo size in px. Items fly in from further out and drift slightly
 * inwards while the stage is pinned.
 */
const LAYOUT: Record<string, { x: number; y: number; w?: number; h?: number }> = {
  strategy: { x: 16, y: 30, w: 250, h: 280 },
  lead: { x: 29, y: 18 },
  build: { x: 82, y: 21, w: 270, h: 210 },
  agent: { x: 87, y: 58 },
  customer: { x: 73, y: 84, w: 250, h: 230 },
  report: { x: 22, y: 79 },
};

const clamp = (value: number) => Math.min(1, Math.max(0, value));
const easeOut = (value: number) => 1 - (1 - value) ** 3;
const range = (value: number, start: number, end: number) => clamp((value - start) / (end - start));

/** Desktop scroll scene; phones and reduced motion get the resting layout. */
export function WhyChooseUsSceneMotion({ labelledBy, statements, heading, items }: WhyChooseUsSceneMotionProps) {
  const sectionRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const statementRefs = useRef<(HTMLDivElement | null)[]>([]);
  const headingRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useCcPrefersReducedMotion();
  const [wide, setWide] = useState(false);
  const animated = wide && !reducedMotion;

  useEffect(() => {
    const media = window.matchMedia("(min-width: 900px)");
    const update = () => setWide(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useCcScrollEffect(() => {
    const section = sectionRef.current;
    const stage = stageRef.current;
    if (!section || !stage || !animated) return;

    const rect = section.getBoundingClientRect();
    const viewport = window.innerHeight;
    // 0 when the section enters from below, 1 when the pinned stage releases.
    const progress = clamp((viewport - rect.top) / rect.height);
    const enter = easeOut(range(progress, 0.06, 0.3));
    const drift = range(progress, 0.3, 1);
    const width = stage.clientWidth;
    const height = stage.clientHeight;

    for (const item of items) {
      const node = itemRefs.current[item.id];
      const spot = LAYOUT[item.id];
      if (!node || !spot) continue;
      const spread = 1.85 - 0.85 * enter - 0.07 * drift;
      const dx = (spot.x / 100 - 0.5) * width * spread;
      const dy = (spot.y / 100 - 0.5) * height * spread;
      node.style.transform = `translate(-50%, -50%) translate3d(${dx.toFixed(1)}px, ${dy.toFixed(1)}px, 0) scale(${(0.9 + 0.1 * enter).toFixed(3)})`;
      node.style.opacity = enter.toFixed(3);
    }

    // Slides: each statement fades in and out in turn, then the heading stays.
    const slides = statements.length;
    const slideSpan = 0.5 / Math.max(1, slides);
    statementRefs.current.forEach((node, index) => {
      if (!node) return;
      const start = 0.2 + index * slideSpan;
      const fadeIn = easeOut(range(progress, start, start + slideSpan * 0.45));
      const fadeOut = easeOut(range(progress, start + slideSpan * 0.75, start + slideSpan));
      node.style.opacity = (fadeIn * (1 - fadeOut)).toFixed(3);
      node.style.transform = `translate(-50%, -50%) translateY(${((1 - fadeIn) * 30 - fadeOut * 30).toFixed(1)}px)`;
    });
    const headIn = easeOut(range(progress, 0.7, 0.82));
    if (headingRef.current) {
      headingRef.current.style.opacity = headIn.toFixed(3);
      headingRef.current.style.transform = `translate(-50%, -50%) translateY(${((1 - headIn) * 30).toFixed(1)}px)`;
      headingRef.current.style.pointerEvents = headIn > 0.5 ? "auto" : "none";
    }
  });

  // Clear inline motion styles when the scene switches to the static layout.
  useEffect(() => {
    if (animated) return;
    for (const node of [...Object.values(itemRefs.current), ...statementRefs.current, headingRef.current]) {
      if (!node) continue;
      node.style.transform = "";
      node.style.opacity = "";
      node.style.pointerEvents = "";
    }
  }, [animated]);

  return (
    <section
      ref={sectionRef}
      className={`cc-wcu-scene${animated ? " cc-wcu-scene--animated" : ""}`}
      aria-labelledby={labelledBy}
      data-cc-nav="dark"
      {...devSectionAttr("why-choose-us")}
    >
      <div className="cc-wcu-scene__frame">
        <div ref={stageRef} className="cc-wcu-scene__stage">
          <div className="cc-wcu-scene__statements">
            {statements.map((statement, index) => (
              <div
                key={index}
                ref={(node) => {
                  statementRefs.current[index] = node;
                }}
                className="cc-wcu-scene__statement"
              >
                {statement}
              </div>
            ))}
          </div>
          <div ref={headingRef} className="cc-wcu-scene__heading">
            {heading}
          </div>
          <div className="cc-wcu-scene__items">
            {items.map((item) => {
              const spot = LAYOUT[item.id];
              return (
                <div
                  key={item.id}
                  ref={(node) => {
                    itemRefs.current[item.id] = node;
                  }}
                  className={`cc-wcu-scene__item cc-wcu-scene__item--${item.kind} cc-wcu-scene__item--${item.id}`}
                >
                  {item.kind === "photo" ? (
                    <span
                      className="cc-wcu-scene__photo"
                      style={{ "--w": `${spot?.w ?? 240}px`, "--h": `${spot?.h ?? 240}px` } as CSSProperties}
                    >
                      <Image src={item.photo.src} alt={item.photo.alt} fill sizes="280px" />
                    </span>
                  ) : (
                    <div aria-hidden="true">{item.card}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
